/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Microsoft Azure", description = "Grant Hightouch access to resources within your Azure account.", lastUpdatedDate = new Date(1731542400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Microsoft Azure",
        description: "Grant Hightouch access to resources within your Azure account.",
        lastUpdatedDate: "Thu Nov 14 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Configure Azure",
          "depth": 0,
          "id": "configure-azure"
        }, {
          "value": "Create an app registration",
          "depth": 1,
          "id": "create-an-app-registration"
        }, {
          "value": "Grant access to Blob Storage",
          "depth": 1,
          "id": "grant-access-to-blob-storage"
        }, {
          "value": "Configure Hightouch",
          "depth": 0,
          "id": "configure-hightouch"
        }, {
          "value": "Azure credential usage",
          "depth": 0,
          "id": "azure-credential-usage"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      a: "a",
      h3: "h3",
      ol: "ol",
      strong: "strong",
      img: "img",
      em: "em"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's Azure integration powers several features:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Workspace deployment on Azure (see ", _jsx(_components.a, {
            href: "/security/regions#supported-regions",
            children: "Regions"
          }), ")"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/security/storage#self-hosted-storage",
            children: "Self-hosted storage via Azure Blob Storage"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/sources/azure-blob",
            children: "Azure Blob Storage"
          }), " source"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/destinations/azure-blob",
            children: "Azure Blob Storage"
          }), " destination"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/security/azure/private-link",
            children: "Azure Private Link"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "configure-azure",
        children: "Configure Azure"
      }), "\n", _jsx(_components.p, {
        children: "Azure configuration includes creating an app registration and granting access to Blob Storage. Once you've done both of these, you can use your Blob Storage information and Azure credentials to connect Hightouch."
      }), "\n", _jsx(_components.h3, {
        id: "create-an-app-registration",
        children: "Create an app registration"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the ", _jsx(_components.a, {
              href: "https://portal.azure.com/",
              children: "Azure Portal"
            }), ", navigate to ", _jsx(_components.strong, {
              children: "Azure Active Directory"
            }), ".\n", _jsx(_components.img, {
              src: "integrations/azure/azure-portal-ad.png",
              alt: "Azure Portal screenshot"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Locate and click ", _jsx(_components.strong, {
              children: "Manage"
            }), " > ", _jsx(_components.strong, {
              children: "App registrations"
            }), " in the left-hand menu.\n", _jsx(_components.img, {
              src: "integrations/azure/azure-portal-app-registration.png",
              alt: "Azure Portal screenshot"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Create a new app registration by selecting ", _jsx(_components.strong, {
              children: "+ New registration"
            }), ". Give the application a descriptive ", _jsx(_components.strong, {
              children: "Name"
            }), ", for example, \"Hightouch-integration-user,\" and leave the ", _jsx(_components.strong, {
              children: "Supported account type"
            }), " as ", _jsx(_components.strong, {
              children: "Accounts in this organizational directory only (Default Directory only - Single tenant)"
            }), ". You don't need a redirect URI for Hightouch storage integration. Click ", _jsx(_components.strong, {
              children: "Register"
            }), " when done.\n", _jsx(_components.img, {
              src: "integrations/azure/azure-portal-create-registration.png",
              alt: "Azure Portal screenshot"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Next, you need an app secret for this account. While on the app registration screen, navigate to ", _jsx(_components.strong, {
              children: "Manage"
            }), " > ", _jsx(_components.strong, {
              children: "Certificates & secrets"
            }), " in the left-hand menu.\nIn the ", _jsx(_components.strong, {
              children: "Client secrets"
            }), " tab, click ", _jsx(_components.strong, {
              children: "New client secret"
            }), ". Enter a ", _jsx(_components.strong, {
              children: "Description"
            }), " and ", _jsx(_components.strong, {
              children: "Expires"
            }), " time frame. Click ", _jsx(_components.strong, {
              children: "Add"
            }), " to create the secret."]
          }), "\n", _jsx(Warning, {
            children: _jsx(_components.p, {
              children: "Make sure to select a sufficient expiry time frame. If this credential expires, Hightouch can't\naccess your Azure Blob Storage, and if your workspace is configured to use Azure for log storage, all syncs will fail."
            })
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "integrations/azure/azure-app-secret.png",
              alt: "Azure Portal screenshot"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Copy and store the secret ", _jsx(_components.strong, {
              children: "Value"
            }), ", ", _jsx(_components.em, {
              children: "not"
            }), " the ", _jsx(_components.strong, {
              children: "Secret ID"
            }), ", after you create it. You won't be able to read the value again later."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/azure/azure-app-secret-value.png",
          alt: "Azure Portal screenshot"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "6",
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to the App ", _jsx(_components.strong, {
            children: "Overview"
          }), " in the left-hand menu. Copy and save the ", _jsx(_components.strong, {
            children: "Application (client) ID"
          }), " and ", _jsx(_components.strong, {
            children: "Directory (tenant) ID"
          }), ". You need both of these and the Client secret value\nto configure Hightouch.\n", _jsx(_components.img, {
            src: "integrations/azure/azure-portal-client-tenant-id.png",
            alt: "Azure Portal screenshot"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "grant-access-to-blob-storage",
        children: "Grant access to Blob Storage"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the ", _jsx(_components.a, {
              href: "https://portal.azure.com/",
              children: "Azure Portal"
            }), ", navigate to your storage account, and then to the Blob Storage container you want to connect to Hightouch. If you don't have a container, ", _jsx(_components.a, {
              href: "https://learn.microsoft.com/en-us/azure/storage/blobs/storage-quickstart-blobs-portal#create-a-container",
              children: "create one"
            }), ".\n", _jsx(_components.img, {
              src: "integrations/azure/azure-container.png",
              alt: "Azure Portal screenshot"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the configuration for your Blob Storage container, go to ", _jsx(_components.strong, {
              children: "Access Control (IAM)"
            }), " in the left-hand menu.\n", _jsx(_components.img, {
              src: "integrations/azure/azure-portal-iam.png",
              alt: "Azure Portal screenshot"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "+Add"
            }), " then ", _jsx(_components.strong, {
              children: "Add role assignment"
            }), ".\n", _jsx(_components.img, {
              src: "integrations/azure/azure-portal-add-assignment.png",
              alt: "Azure Portal screenshot"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select the role you want to grant to the Hightouch integration. To use the integration for storage, we suggest\nusing the ", _jsx(_components.strong, {
              children: "Storage Blob Data Contributor"
            }), " role.\nIf you want to assign more granular permissions, refer to the for ", _jsx(_components.a, {
              href: "/security/storage",
              children: "storage"
            }), " documentation\nand consult your Active Directory administrator.\nOnce you've selected the role, click ", _jsx(_components.strong, {
              children: "Next"
            }), " to assign ", _jsx(_components.strong, {
              children: "Members"
            }), " to the role."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "integrations/azure/azure-portal-storage-blob-role.png",
              alt: "Azure Portal screenshot"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Keep ", _jsx(_components.strong, {
              children: "User, group, or service principal"
            }), " as the access assignment. Click ", _jsx(_components.strong, {
              children: "+ Select members"
            }), " and search for and select the Hightouch integration user you created during ", _jsx(_components.a, {
              href: "#create-an-app-registration",
              children: "app registration"
            }), ".\n", _jsx(_components.img, {
              src: "integrations/azure/azure-portal-role-assignment.png",
              alt: "Azure Portal screenshot"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Review + assign"
            }), " to confirm. The Hightouch user should now have access to your Azure Blob Storage."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "configure-hightouch",
        children: "Configure Hightouch"
      }), "\n", _jsxs(_components.p, {
        children: ["To configure Hightouch to use the Azure integration user,\ngo to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/settings/cloud-providers",
          children: _jsx(_components.strong, {
            children: "Cloud providers"
          })
        }), " tab on the Settings page. Click ", _jsx(_components.strong, {
          children: "Add cloud provider"
        }), ", and select ", _jsx(_components.strong, {
          children: "Microsoft Azure"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/cloud-providers/add-new-credential.png",
          alt: "Adding a new cloud provider in Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Give your credential a ", _jsx(_components.strong, {
          children: "Display name"
        }), ", and enter the ", _jsx(_components.strong, {
          children: "Tenant ID"
        }), ", ", _jsx(_components.strong, {
          children: "Client ID"
        }), ", and ", _jsx(_components.strong, {
          children: "Client secret"
        }), " from your Azure configuration."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/azure/azure-portal-client-tenant-id.png",
          alt: "Azure Portal screenshot"
        })
      }), "\n", _jsx(_components.h2, {
        id: "azure-credential-usage",
        children: "Azure credential usage"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Microsoft Azure credentials in Hightouch, you can select Microsoft Azure when setting up ", _jsx(_components.a, {
          href: "/security/storage",
          children: "external storage"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/azure/use-1-external-storage.png",
          alt: "Hightouch app screenshot"
        })
      })]
    });
  }
}
export default MDXContent;
